import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useRef } from "react";

interface TokenCache {
    token: string;
    expiresAt: number;
}

export const useApiService = () => {
    const { getAccessTokenSilently } = useAuth0();
    const tokenCache = useRef<TokenCache | null>(null);

    const fetchWithAuth = useCallback(async (endpoint: string, options: RequestInit = {}) => {
        try {
            // Check if we have a valid cached token
            const now = Date.now();
            if (!tokenCache.current || now >= tokenCache.current.expiresAt) {
                const token = await getAccessTokenSilently();
                // Cache the token with a 15-minute expiration (900000 ms)
                tokenCache.current = {
                    token,
                    expiresAt: now + 900000
                };
            }

            const rootUrl = process.env.NODE_ENV === "production" ? process.env.REACT_APP_BACKEND_URL : window.location.origin;
            
            const response = await fetch(`${rootUrl}${endpoint}`, {
                ...options,
                headers: {
                    ...options.headers,
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenCache.current.token}`,
                },
            });

            return response;
        } catch (error) {
            console.error('Error in fetchWithAuth:', error);
            // Clear the token cache on error
            tokenCache.current = null;
            throw error;
        }
    }, [getAccessTokenSilently]);

    return {
        fetchWithAuth,
    };
}; 